<script setup lang="ts">
import type { SessionView } from '@/shared/contracts';
import { FfDoodle, FfWordmark } from './index';
import { useTheme } from '@/composables/useTheme';
import { useVariant } from '@/composables/useVariant';
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useApi, api, refreshData, clearData, navigateTo, useRoute, useRouter } from '@/runtime';

const props = defineProps<{ session: SessionView; divisionName: string }>();
const route = useRoute();
const router = useRouter();
const { theme, toggle: toggleTheme } = useTheme();
const { variant, toggle: toggleVariant } = useVariant();

const open = ref<null | 'search' | 'bell' | 'me'>(null);
const close = () => { open.value = null; };
watch(() => route.fullPath, () => { open.value = null; });

const { data: home } = await useApi('/api/home');
const { data: unread } = await useApi<{ unreadCount: number }>('/api/notifications/unread', { key: 'notifications-unread', default: () => ({ unreadCount: 0 }) });
const coins = computed(() => home.value?.points?.coins ?? 0);
const xp = computed(() => home.value?.points?.xp ?? 0);
const inboxCount = computed(() => home.value?.tasks?.inboxCount ?? 0);
const reviewCount = computed(() => home.value?.tasks?.reviewCount ?? 0);

const ORDER = ['/', '/people', '/tasks', '/review', '/projects', '/events', '/rating', '/shop', '/kudos', '/points'];
const items = computed(() => {
  const byTo = new Map(props.session.nav.map(item => [item.to, item]));
  const ordered = ORDER.filter(to => byTo.has(to)).map((to, index) => ({ ...byTo.get(to)!, num: String(index + 1).padStart(2, '0') }));
  const rest = props.session.nav.filter(item => !ORDER.includes(item.to) && item.to !== '/admin');
  return { ordered, rest, admin: byTo.get('/admin') ?? null };
});
function badgeFor(to: string): number | null {
  if (to === '/tasks' && inboxCount.value > 0) return inboxCount.value;
  if (to === '/review' && reviewCount.value > 0) return reviewCount.value;
  return null;
}
const isActive = (to: string) => to === '/' ? route.path === '/' : route.path === to || route.path.startsWith(`${to}/`);

const todayLabel = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'Europe/Moscow' }).format(new Date()).replace('.', '');
const initial = computed(() => (props.session.user.name.trim()[0] ?? '•').toUpperCase());

const query = ref('');
const searching = ref(false);
const searchError = ref('');
interface SearchResults {
  people: { items: { id: string; name: string }[] };
  tasks: { items: { id: string; title: string }[] };
  events: { items: { id: string; title: string }[] };
}
const results = ref<SearchResults | null>(null);
let searchTimer: ReturnType<typeof setTimeout> | null = null;
watch(query, value => {
  if (searchTimer) clearTimeout(searchTimer);
  results.value = null;
  searchError.value = '';
  if (value.trim().length < 2) { searching.value = false; return; }
  searching.value = true;
  searchTimer = setTimeout(async () => {
    try {
      results.value = await api<SearchResults>('/api/search', { query: { q: value.trim() } });
    } catch { searchError.value = 'Поиск не отвечает. Попробуйте ещё раз.'; }
    finally { searching.value = false; }
  }, 250);
});
function goSearch() {
  if (query.value.trim().length >= 2) void router.push({ path: '/search', query: { q: query.value.trim() } });
}
function openSearch() { open.value = 'search'; query.value = ''; results.value = null; nextTick(() => document.getElementById('ff-search-input')?.focus()); }
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') { close(); return; }
  const target = event.target as HTMLElement | null;
  if (event.key === '/' && target && !/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) && !target.isContentEditable) {
    event.preventDefault();
    open.value === 'search' ? close() : openSearch();
  }
}
onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));

interface InboxRow { id: string; title: string; body: string | null; href: string | null; createdAt: string }
const { data: inbox, refresh: refreshInbox } = await useApi<{ unreadCount: number; notifications: InboxRow[] }>('/api/notifications', { immediate: false });
watch(open, value => { if (value === 'bell') void refreshInbox(); });
const busy = ref(false);
const notice = ref('');
async function readAll() {
  busy.value = true;
  try {
    await api('/api/notifications/read', { method: 'POST', body: {} });
    await Promise.all([refreshInbox(), refreshData('notifications-unread')]);
  } catch { notice.value = 'Не получилось. Попробуйте ещё раз.'; }
  finally { busy.value = false; }
}
async function signOut() {
  busy.value = true;
  try { await api('/api/auth/logout', { method: 'POST', body: {} }); clearData(); await navigateTo('/login'); }
  catch { notice.value = 'Не удалось выйти. Попробуйте ещё раз.'; }
  finally { busy.value = false; }
}
</script>
<template>
  <header class="ff-header">
    <div class="ff-header-inner">
      <div class="ff-toprow">
        <RouterLink to="/" class="ff-brand" aria-label="На главную">
          <span aria-hidden="true" class="ff-brand-full"><FfWordmark :size="0.3" /></span>
          <span aria-hidden="true" class="ff-brand-mini"><FfWordmark :size="0.2" /></span>
          <span class="ff-brand-sub"><strong>таск-трекер</strong><small>студсовет финфака</small></span>
        </RouterLink>
        <button class="ff-searchbtn" aria-label="Поиск по порталу" @click="openSearch">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <span class="ff-searchbtn-text">найти задачу, активиста или событие</span>
          <kbd aria-hidden="true">/</kbd>
        </button>
        <div class="ff-topspacer" />
        <button class="ff-searchicon" aria-label="Поиск по порталу" @click="openSearch">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
        </button>
        <RouterLink to="/tasks?new=1" class="ff-newtask">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          задача
        </RouterLink>
        <div class="ff-pills">
          <RouterLink to="/points" class="ff-pill ff-pill-coins" title="Монеты — тратятся в магазине"><span data-spin><FfDoodle name="brush-star" :width="18" color="#D07B45" /></span>{{ coins }}</RouterLink>
          <RouterLink to="/points" class="ff-pill ff-pill-xp" title="Опыт — растит уровень"><span data-twinkle><FfDoodle name="sparkle" :width="11" color="#9698F0" :rotate="20" /></span>{{ xp }}</RouterLink>
        </div>
        <div class="ff-iconbtns">
          <button class="ff-iconbtn" title="Уведомления" :aria-label="unread?.unreadCount ? `Уведомления, непрочитанных: ${unread.unreadCount}` : 'Уведомления'" @click="open = open === 'bell' ? null : 'bell'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9a6 6 0 1 1 12 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9z" /><path d="M10 20a2.2 2.2 0 0 0 4 0" /></svg>
            <span v-if="unread?.unreadCount" class="ff-badge-dot">{{ unread.unreadCount > 99 ? '99+' : unread.unreadCount }}</span>
          </button>
          <button class="ff-iconbtn" :title="theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'" :aria-label="theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'" @click="toggleTheme">
            <svg v-if="theme !== 'dark'" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" /></svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EFD983" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4.2" /><path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M5.3 18.7l1.6-1.6M17.1 6.9l1.6-1.6" /></svg>
          </button>
          <button class="ff-avatar" title="Профиль" aria-label="Меню профиля" @click="open = open === 'me' ? null : 'me'">{{ initial }}</button>
        </div>
      </div>
      <nav class="ff-navrow" aria-label="Разделы портала">
        <template v-for="item in items.ordered" :key="item.to">
          <RouterLink v-if="route.path === item.to || (item.to !== '/' && route.path.startsWith(item.to + '/'))" :to="item.to" class="ff-navitem ff-navitem-active" :aria-current="'page'">
            <span class="ff-navnum">{{ item.num }}</span><span>{{ item.label }}</span>
          </RouterLink>
          <RouterLink v-else :to="item.to" class="ff-navitem">
            <span class="ff-navnum">{{ item.num }}</span><span>{{ item.label }}</span>
            <span v-if="badgeFor(item.to) !== null" class="ff-navbadge">{{ badgeFor(item.to) }}</span>
          </RouterLink>
        </template>
        <div class="ff-navspacer" />
        <span class="ff-navdate" aria-hidden="true">{{ todayLabel }}</span>
        <RouterLink v-if="items.admin" to="/admin" class="ff-navitem" :class="{ 'ff-navitem-active': isActive('/admin') }" :aria-current="isActive('/admin') ? 'page' : undefined">
          <span class="ff-navnum">11</span><span>Админка</span>
        </RouterLink>
      </nav>
    </div>

    <div v-if="open" class="ff-overlay" @click="close" />
    <div v-if="open === 'search'" data-drop role="dialog" aria-modal="false" aria-label="Быстрый поиск" class="ff-pop ff-pop-search">
      <div class="ff-pop-searchbar">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
        <input id="ff-search-input" v-model="query" type="search" placeholder="задача, активист, событие, группа…" autocomplete="off" @keydown.enter="goSearch" />
      </div>
      <p v-if="searching" class="ff-pop-hint" role="status">Ищем…</p>
      <p v-else-if="searchError" class="ff-pop-hint" role="alert">{{ searchError }}</p>
      <template v-else-if="results">
        <div v-for="group in [{ key: 'people', label: 'Люди', to: (id: string) => `/people/${id}`, title: (row: { name?: string; title?: string }) => row.name ?? row.title ?? '' }, { key: 'tasks', label: 'Задачи', to: (id: string) => `/tasks/${id}`, title: (row: { name?: string; title?: string }) => row.title ?? row.name ?? '' }, { key: 'events', label: 'События', to: (id: string) => `/events/${id}`, title: (row: { name?: string; title?: string }) => row.title ?? row.name ?? '' }] as const" :key="group.key">
          <RouterLink v-for="row in (results[group.key]?.items ?? []).slice(0, 3)" :key="row.id" :to="group.to(row.id)" class="ff-pop-row">{{ group.title(row) }}</RouterLink>
        </div>
        <p v-if="!results.people.items.length && !results.tasks.items.length && !results.events.items.length" class="ff-pop-hint">Ничего не нашлось — Enter откроет полный поиск.</p>
      </template>
    </div>
    <div v-if="open === 'bell'" data-drop role="dialog" aria-modal="false" aria-label="Уведомления" class="ff-pop ff-pop-right">
      <p v-if="notice" class="ff-pop-hint" role="alert">{{ notice }}</p>
      <div v-if="(inbox?.notifications ?? []).length" class="ff-pop-list">
        <RouterLink v-for="row in (inbox?.notifications ?? []).slice(0, 8)" :key="row.id" :to="row.href ?? '/notifications'" class="ff-pop-row"><strong>{{ row.title }}</strong><small>{{ row.body }}</small></RouterLink>
      </div>
      <p v-else class="ff-pop-hint">Пока тихо.</p>
      <div class="ff-pop-actions"><button class="ff-linkbtn" :disabled="busy || !inbox?.unreadCount" @click="readAll">Прочитать все</button><RouterLink to="/notifications" class="ff-linkbtn">Открыть все</RouterLink></div>
    </div>
    <div v-if="open === 'me'" data-drop role="dialog" aria-modal="false" aria-label="Меню профиля" class="ff-pop ff-pop-right">
      <RouterLink to="/profile" class="ff-pop-row ff-pop-row-between">Личный кабинет <span aria-hidden="true">→</span></RouterLink>
      <RouterLink to="/profile" class="ff-pop-row">Изменить профиль</RouterLink>
      <RouterLink to="/settings-notifications" class="ff-pop-row">Настройки уведомлений</RouterLink>
      <div class="ff-pop-row ff-pop-row-between"><span>Вариант: {{ variant }}</span><button class="ff-linkbtn" @click="toggleVariant">{{ variant === 'B' ? 'Включить А' : 'Включить Б' }}</button></div>
      <button class="ff-pop-row ff-pop-danger" :disabled="busy" @click="signOut">Выйти</button>
    </div>
  </header>
</template>
<style scoped>
.ff-header { position: relative; isolation: isolate; z-index: 30; color: var(--ff-ink, #414046); background: color-mix(in srgb, var(--ff-bg, #FAF8F2) 94%, transparent); backdrop-filter: saturate(1.2) blur(6px); border-bottom: 1px solid color-mix(in srgb, var(--ff-ink, #414046) 14%, transparent); }
.ff-header-inner { position: relative; max-width: 1440px; margin: 0 auto; padding: 0 32px; box-sizing: border-box; }
.ff-toprow { height: 68px; display: flex; align-items: center; gap: 20px; }
.ff-brand { display: flex; align-items: center; gap: 12px; text-decoration: none; flex-shrink: 1; min-width: 0; color: inherit; }
.ff-brand-mini { display: none; }
.ff-brand-sub { display: flex; flex-direction: column; gap: 3px; padding-left: 12px; border-left: 1px solid color-mix(in srgb, var(--ff-ink, #414046) 18%, transparent); }
.ff-brand-sub strong { font-weight: 600; font-size: 11.5px; line-height: 1; letter-spacing: .07em; text-transform: uppercase; }
.ff-brand-sub small { font-size: 11px; line-height: 1; color: var(--ff-ink3, #6F6D75); }
.ff-searchbtn { flex: 1; max-width: 440px; min-width: 200px; height: 42px; display: flex; align-items: center; gap: 10px; padding: 0 8px 0 16px; border-radius: 999px; border: 0; background: var(--ff-card, #F4EFE5); cursor: pointer; font: inherit; color: var(--ff-ink3, #6F6D75); font-size: 14px; text-align: left; }
.ff-searchbtn:hover { box-shadow: inset 0 0 0 1.5px #E65C87; }
.ff-searchbtn-text { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ff-searchbtn kbd { font-size: 12px; color: var(--ff-ink, #414046); background: var(--ff-bg, #FAF8F2); border-radius: 8px; padding: 4px 8px; box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--ff-ink, #414046) 16%, transparent); font-family: inherit; }
.ff-topspacer { flex: 1; }
.ff-searchicon { display: none; width: 42px; height: 42px; align-items: center; justify-content: center; border: 0; border-radius: 999px; background: transparent; color: inherit; cursor: pointer; flex-shrink: 0; }
.ff-searchicon:hover { background: var(--ff-card, #F4EFE5); }
.ff-newtask { height: 42px; display: flex; align-items: center; gap: 8px; padding: 0 18px 0 14px; border: 0; border-radius: 999px; background: var(--ff-ink, #414046); color: var(--ff-card, #F4EFE5); font: inherit; font-weight: 600; font-size: 14px; cursor: pointer; flex-shrink: 0; text-decoration: none; transition: background .2s; }
.ff-newtask:hover { background: #E65C87; }
.ff-pills { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.ff-pill { height: 38px; display: flex; align-items: center; gap: 7px; padding: 0 12px 0 9px; border-radius: 999px; text-decoration: none; color: var(--ff-ink, #414046); font-weight: 600; font-size: 14px; }
.ff-pill-coins { background: rgba(239, 217, 131, .45); }
.ff-pill-coins:hover { background: var(--ff-butter-s, #EFD983); }
.ff-pill-xp { background: rgba(150, 152, 240, .2); }
.ff-pill-xp:hover { background: rgba(150, 152, 240, .4); }
.ff-iconbtns { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }
.ff-iconbtn { position: relative; width: 42px; height: 42px; display: flex; align-items: center; justify-content: center; border: 0; border-radius: 999px; background: transparent; color: inherit; cursor: pointer; }
.ff-iconbtn:hover { background: var(--ff-card, #F4EFE5); }
.ff-badge-dot { position: absolute; top: 5px; right: 4px; min-width: 18px; height: 18px; padding: 0 5px; box-sizing: border-box; border-radius: 999px; background: #E65C87; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 2px var(--ff-bg, #FAF8F2); }
.ff-avatar { margin-left: 6px; width: 42px; height: 42px; border-radius: 999px; border: 0; padding: 0; background: var(--ff-ink, #414046); color: var(--ff-card, #F4EFE5); font-family: 'TD Mars'; font-size: 12px; line-height: 1; cursor: pointer; box-shadow: 0 0 0 2px var(--ff-bg, #FAF8F2), 0 0 0 3.5px #E65C87; transition: transform .2s; }
.ff-avatar:hover { transform: rotate(-6deg) scale(1.05); }
.ff-navrow { height: 50px; display: flex; align-items: center; gap: 2px; overflow-x: auto; scrollbar-width: none; }
.ff-navrow::-webkit-scrollbar { display: none; }
.ff-navitem { flex-shrink: 0; height: 36px; display: flex; align-items: center; gap: 6px; padding: 0 13px; border-radius: 999px; text-decoration: none; color: var(--ff-ink, #414046); }
.ff-navitem:hover { background: var(--ff-card, #F4EFE5); }
.ff-navitem-active { background: var(--ff-card, #F4EFE5); box-shadow: inset 0 0 0 1.5px #E65C87; }
.ff-navnum { font-size: 10px; font-weight: 600; font-variant-numeric: tabular-nums; }
.ff-navitem-active > .ff-navnum { color: #E65C87; }
.ff-navitem:not(.ff-navitem-active) > .ff-navnum { color: var(--ff-ink4, #8E8C93); }
.ff-navitem > span:nth-child(2) { font-size: 14.5px; font-weight: 500; line-height: 1; }
.ff-navbadge { min-width: 18px; height: 18px; padding: 0 5px; box-sizing: border-box; border-radius: 999px; background: var(--ff-ink, #414046); color: var(--ff-card, #F4EFE5); font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.ff-navspacer { flex: 1; min-width: 16px; }
.ff-navdate { flex-shrink: 0; font-family: 'Angst'; font-weight: 400; font-size: 17px; line-height: 1; text-transform: uppercase; color: var(--ff-ink3, #6F6D75); padding-right: 14px; margin-right: 6px; border-right: 1px solid color-mix(in srgb, var(--ff-ink, #414046) 18%, transparent); }
.ff-overlay { position: fixed; inset: 0; z-index: 40; }
.ff-pop { position: absolute; z-index: 41; background: var(--ff-bg, #FAF8F2); border-radius: 24px; box-shadow: 0 2.553px 2.553px rgba(0, 0, 0, .25), inset 0 0 0 1.5px #E65C87; overflow: hidden; }
.ff-pop-search { top: 8px; left: 250px; width: min(560px, calc(100vw - 300px)); }
.ff-pop-right { top: 8px; right: 32px; width: min(380px, calc(100vw - 64px)); padding: 12px; }
.ff-pop-searchbar { height: 52px; display: flex; align-items: center; gap: 10px; padding: 0 18px; border-bottom: 1px solid color-mix(in srgb, var(--ff-ink, #414046) 12%, transparent); }
.ff-pop-searchbar input { flex: 1; border: 0; outline: 0; background: transparent; font: inherit; font-size: 15px; color: var(--ff-ink, #414046); }
.ff-pop-row { display: flex; align-items: center; gap: 12px; padding: 10px; border-radius: 14px; text-decoration: none; color: var(--ff-ink, #414046); font-size: 14px; width: 100%; box-sizing: border-box; border: 0; background: transparent; cursor: pointer; font: inherit; text-align: left; }
.ff-pop-row:hover { background: var(--ff-card, #F4EFE5); }
.ff-pop-row strong { font-weight: 600; }
.ff-pop-row small { display: block; color: var(--ff-ink3, #6F6D75); font-size: 12px; }
.ff-pop-row-between { justify-content: space-between; }
.ff-pop-danger { color: var(--ff-pink-ink, #C2456B); font-weight: 600; }
.ff-pop-hint { padding: 12px 18px; color: var(--ff-ink3, #6F6D75); font-size: 13px; }
.ff-pop-list { display: grid; }
.ff-pop-actions { display: flex; gap: 8px; padding: 4px 10px 8px; }
.ff-linkbtn { display: inline-flex; padding: 11px 12px; border-radius: 14px; text-decoration: none; color: var(--ff-ink, #414046); font-size: 14px; border: 0; background: transparent; cursor: pointer; font: inherit; }
.ff-linkbtn:hover { background: var(--ff-card, #F4EFE5); }
.ff-linkbtn:disabled { opacity: .5; cursor: default; }
@media (max-width: 900px) {
  .ff-navrow { display: none; }
  .ff-searchbtn, .ff-pills, .ff-newtask { display: none; }
  .ff-searchicon { display: flex; }
  .ff-toprow { gap: 12px; }
  .ff-brand-full { display: none; }
  .ff-brand-mini { display: block; }
  .ff-pop-search { left: 16px; width: calc(100vw - 32px); }
  .ff-pop-right { right: 16px; }
  .ff-header-inner { padding: 0 16px; }
}
</style>
